import { Badge } from '@/components/ui/badge';
import { CircleCheck, CircleX, EllipsisVertical, FilterIcon, IterationCcw, PencilLine } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Trash2 } from 'lucide-react';
import { ColumnDef } from '@tanstack/react-table';
import { getUsers } from '@/services/Network';
import type { UserResponseSchema, UserToggleActivationRequestAction } from '@/types/user.types';
import { useToggleUserActivation } from '@/services/userService';
import { capitalizeFirstLetter, convertISODateToMMDDYYYY } from '@/lib/utils';
import { DropdownMenu } from '@radix-ui/react-dropdown-menu';
import { DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import AlertBox from '@/components/AlertBox';
import { useContext, useEffect, useMemo, useState } from 'react';
import { toast } from '@/components/ui/use-toast';
import { DataTable } from '@/components/DataTable';
import { OneLineText } from '@/components/OneLineText';
import { UserDetails } from './UserDetails';
import { NavigationBlockerContext } from '@/context/NavigationBlockerContext';

export const Users = () => {
  const [, setSearchParam] = useSearchParams();
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [action, setAction] = useState<{
    action?: UserToggleActivationRequestAction;
    user: UserResponseSchema;
  } | null>(null);
  const [updatedUser, setUpdatedUser] = useState<UserResponseSchema | null>(null);
  const [reinvitedUserId, setReinvitedUserId] = useState<string | null>(null);
  const { setIsDirty } = useContext(NavigationBlockerContext);
  const { mutate: changeStatus, status: toggleStatus } = useToggleUserActivation();
  const handleDialogNo = (e: React.MouseEvent) => {
    e.stopPropagation();
    setAction(null);
  };

  const handleDialogYes = (
    e: React.MouseEvent,
    selectedAction?: { user: UserResponseSchema; action: UserToggleActivationRequestAction }
  ) => {
    e.stopPropagation();
    if (selectedAction ?? action) {
      const userId = selectedAction?.user.userId ?? action?.user.userId;
      const userAction = selectedAction?.action ?? action?.action;
      const user = selectedAction?.user ?? action?.user;
      if (userId && userAction) {
        changeStatus(
          {
            id: userId,
            data: {
              action: userAction,
            },
          },
          {
            onSuccess: (data) => {
              if (user) {
                setUpdatedUser({
                  ...user,
                  // The API returns null status for some actions (e.g. CANCEL); pass it through unchanged.
                  status: data.status!,
                  deactivatedAt: new Date().toISOString(),
                  ...(action?.action === 'REINVITE' && { userId: user.userId }),
                });
              }
              if (data.userId) setReinvitedUserId(data.userId);
              toast({
                variant: 'default',
                description: data.message,
                className: 'h-18 w-76 mr-0',
                duration: 3000,
              });
              setAction(null);
            },
            onError: (e) => {
              toast({
                variant: 'destructive',
                description: e.error.details as string,
                duration: 3000,
              });
              setAction(null);
            },
          }
        );
      }
    }
  };

  const columns: ColumnDef<UserResponseSchema>[] = useMemo(
    () => [
      {
        accessorKey: 'userId',
        header: 'User ID',
        size: 180,

        cell: ({ cell }) => {
          return (
            <div className="w-full h-full cursor-pointer min-w-[170px] flex flex-col">
              <span>{cell.row.original.code}</span>
              {['INACTIVE', 'ACTIVE'].includes(cell.row.original.status) && (
                <span className="text-xs text-gray-500">{`Created on: ${convertISODateToMMDDYYYY(cell.row.original.createdAt)}`}</span>
              )}
              {cell.row.original.status === 'INACTIVE' && (
                <span className="text-xs text-error">{`Deactivated on: ${convertISODateToMMDDYYYY(cell.row.original.deactivatedAt)}`}</span>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'userName',
        header: 'User Name',
        enableSorting: true,
        size: 180,
        cell: ({ cell }) => {
          return (
            <OneLineText
              className="min-w-[150px] flex mr-5"
              text={cell.row.original.firstName + ' ' + cell.row.original.lastName}
            />
          );
        },
      },
      {
        accessorKey: 'roleId',
        header: 'Role',
        enableSorting: true,
        size: 120,
        cell: ({ cell }) => {
          return <span className="min-w-[100px] flex mr-5">{capitalizeFirstLetter(cell.row.original.roles.name)}</span>;
        },
      },
      {
        accessorKey: 'email',
        header: 'Email Address',
        cell: ({ cell }) => {
          return <OneLineText className="mr-5 min-w-[200px]" text={cell.getValue() as string} />;
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        enableSorting: true,
        cell: ({ cell }) => {
          const status = cell.row.original.status;
          if (status === 'PENDING') {
            return <Badge className={`bg-[#ffeebf] text-[#896010] hover:bg-[#ffeebf] mr-6`}>Pending</Badge>;
          } else if (status === 'EXPIRED') {
            return <Badge className={`bg-[#FDEBEB] hover:bg-[#FDEBEB] text-error mr-6`}>Expired</Badge>;
          } else if (status === 'ACTIVE') {
            return <Badge className={`bg-[#D5F4E3] text-[#1B8652] hover:bg-[#D5F4E3] mr-6`}>Active</Badge>;
          } else if (status === 'INACTIVE') {
            return <Badge className={`bg-[#F5F5F5] text-[#676767] hover:bg-[#F5F5F5] mr-6`}>Inactive</Badge>;
          }
        },
      },
      {
        accessorKey: 'actions',
        header: 'Action',
        cell: ({ row }) => {
          const status = row.original.status;
          const user = row.original;
          if (updatedUser && updatedUser.userId === row.original.userId) {
            row.original = updatedUser;
            setUpdatedUser(null);
            if (reinvitedUserId) {
              row.original.userId = reinvitedUserId;
              setReinvitedUserId(null);
            }
          }
          return (
            <>
              <div className="min-w-[100px]">
                {status === 'EXPIRED' ? (
                  <div
                    className="flex items-center  space-x-2 group action w-fit"
                    onClick={(e) => {
                      e.preventDefault();
                      setAction({ action: 'REINVITE', user });
                    }}
                  >
                    <IterationCcw color="#1551B2" className="h-4 w-4" />
                    <span
                      data-testid="resend-invite"
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-focuscolor font-[500] duration-300 ease-in-out"
                    >
                      Resend
                    </span>
                  </div>
                ) : status === 'PENDING' ? (
                  <div
                    className="flex items-center  space-x-2 group action w-fit"
                    onClick={(e) => {
                      e.preventDefault();
                      setAction({ action: 'CANCEL', user });
                    }}
                  >
                    <Trash2 color="#A41919" className="h-4 w-4" />
                    <span
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-error font-[500] duration-300 ease-in-out"
                      data-testid="cancel-invite"
                    >
                      Delete
                    </span>
                  </div>
                ) : (
                  <>
                    <DropdownMenu>
                      <DropdownMenuTrigger className="focus:outline-none action w-6 min-[50px]">
                        <EllipsisVertical color="#969696" className="h-4 w-4 " />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {status === 'ACTIVE' && (
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.preventDefault();
                              navigate(`/users/update/${user.userId}`);
                            }}
                          >
                            <div className="flex justify-start items-center gap-2 w-full text-[12px] text-primary font-[500]">
                              <PencilLine className="h-4 w-4" color="#0A2240" />
                              <span>Edit User</span>
                            </div>
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem>
                          {status === 'ACTIVE' ? (
                            <div
                              className="flex justify-start items-center action gap-2 w-full text-[12px] text-primary font-[500]"
                              onClick={() => {
                                setAction({ action: 'DEACTIVATE', user });
                              }}
                            >
                              <CircleX color="#A41919" className="h-4 w-4" />
                              <span className="text-error" data-testid="deactivate-user">
                                Deactivate User
                              </span>
                            </div>
                          ) : (
                            <div
                              className="flex justify-start items-center action gap-2 w-full text-[12px] text-primary font-[500]"
                              onClick={(e) => {
                                handleDialogYes(e, { action: 'ACTIVATE', user });
                              }}
                            >
                              <CircleCheck color="#37AE74" className="h-4 w-4" />
                              <span className="text-[#37AE74]">Activate User</span>
                            </div>
                          )}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </>
                )}
              </div>
            </>
          );
        },
        size: 75,
      },
    ],
    [updatedUser, reinvitedUserId]
  );

  const navigate = useNavigate();

  const handleRowClick = (row: UserResponseSchema, e: React.MouseEvent) => {
    const targetElement = e.target as HTMLElement;
    const closestActionElement = targetElement.closest('.action');
    if (!closestActionElement && !targetElement.classList.contains('modal')) {
      setSearchParam({ userId: row.userId });
      setSelectedUserId(row.userId);
    }
  };
  useEffect(() => {
    setIsDirty(false);
  }, []);
  return (
    <>
      <DataTable
        columns={columns}
        apiGet={getUsers}
        onRowClick={handleRowClick}
        toolbar={{
          toolBarTitle: 'Users',
          addButton: true,
          addButtonTitle: 'Invite User',
          addOnClick: () => navigate('/users/invites/add'),
          filters: {
            icon: <FilterIcon className="h-4 w-4 text-[#969696]" />,
            groups: [
              {
                label: 'Status',
                name: 'statusFilter',
                options: [
                  { label: 'All', value: null },
                  { label: 'Invited Users', value: 'INVITED' },
                  { label: 'Activated Users', value: 'ACTIVATED' },
                ],
              },
            ],
          },
        }}
        search={[
          {
            label: 'Search Users by ID',
            name: 'code',
            placeholder: 'Search User by ID ',
          },
          {
            label: 'Search Users by Name',
            name: 'userName',
            placeholder: 'Search User by Name ',
          },
          {
            label: 'Search Users by Email Address',
            name: 'email',
            placeholder: 'Search User by Email Address',
          },
        ]}
      />

      {selectedUserId && (
        <UserDetails
          open={true}
          onCloseClick={() => {
            setSelectedUserId(null);
            setSearchParam({});
          }}
          userId={selectedUserId}
        />
      )}

      {action?.action && ['REINVITE', 'CANCEL', 'DEACTIVATE', 'ACTIVATE'].includes(action?.action) && (
        <AlertBox
          open={true}
          title={
            action.action === 'REINVITE'
              ? 'Are you sure you want to resend?'
              : action.action === 'CANCEL'
                ? 'Are you sure you want to cancel?'
                : 'Are you sure you want to deactivate?'
          }
          description={
            action.action === 'REINVITE'
              ? `This action will resend the invite to ${action.user.email}.`
              : action.action === 'CANCEL'
                ? `This action will cancel the invite sent to ${action.user.email}.`
                : `Deactivating ${action.user.firstName} ${action.user.lastName} will remove access but, the data will remain stored in the database.`
          }
          cancelBtnLabel="No"
          confirmBtnLabel="Yes"
          cancelBtnClick={handleDialogNo}
          confirmBtnClick={handleDialogYes}
          isLoading={toggleStatus === 'pending'}
          warning={action.action === 'DEACTIVATE' || action.action === 'CANCEL'}
        />
      )}
    </>
  );
};

export default Users;
